import { TRANSFORMS } from '@/components/Transforms';

/**
 * Apply transforms to raw data value (for export files)
 * Uses the same transform functions as the UI component
 * @param {any} value - The raw value
 * @param {array} transforms - Array of transforms to apply
 * @param {object} row - The full row data (for context-dependent transforms)
 * @returns {string|number|any} - The transformed value
 */
const applyTransformsToValue = (value, transforms = [], row = {}) => {
  if (!Array.isArray(transforms) || transforms.length === 0) {
    return value;
  }

  let output = value;

  transforms.forEach((transform) => {
    const { func = '', params = {} } = transform;
    const formattedFunc = String(func).toLowerCase();
    const transformFunction = TRANSFORMS[formattedFunc];

    if (typeof transformFunction === 'function') {
      output = transformFunction(output, params, row);
    }
  });

  // Extract text from JSX elements for export
  if (output && typeof output === 'object' && output.type) {
    // If it's a JSX element, try to extract the text content
    if (output.props && output.props.children) {
      output = String(output.props.children);
    } else {
      output = String(output);
    }
  }

  return output;
};

const buildExportData = (obj) => {
  const {
    ruleId,
    exportRuleData: ruleData,
    exportFileTitles: fileTitles,
    tableData: exportTableData,
    rows = [],
    fileType = 'excel',
    query = {},
    overrideColumns = null,
    overrideData = null,
    variableName = null,
    columns,
    visiblecolumnsTransforms,
  } = obj;

  const tableKey = ruleData.find((item) => item._id === ruleId)?.key || '';
  const fileTitleBase = fileTitles[tableKey];

  const tableColumns = overrideColumns || exportTableData?.[ruleId]?.columns || [];
  let requestColumns = tableColumns.filter((column) => column.title !== 'פעולות');

  if (columns) {
    requestColumns = columns
      .map((colKey) => {
        const col = requestColumns.find((c) => c.accessor === colKey);
        if (!col) return null;
        return { ...col, isVisible: true };
      })
      .filter(Boolean);
  }

  const dataToExport = overrideData || rows;

  // Apply transforms to data rows
  const transformedData = dataToExport.map((row) => {
    const transformedRow = { ...row };

    requestColumns.forEach((column) => {
      const { accessor, transforms } = column;
      const activeTransforms = visiblecolumnsTransforms?.[accessor] || transforms;

      // eslint-disable-next-line max-len
      if (accessor && activeTransforms && Array.isArray(activeTransforms) && activeTransforms.length > 0) {
        const originalValue = row[accessor];
        transformedRow[accessor] = applyTransformsToValue(originalValue, activeTransforms, row);
      }
    });

    return transformedRow;
  });

  const exportData = {
    fileType,
    columns: requestColumns,
    titleArray: [''],
    data: transformedData,
  };

  if (query && Object.keys(query).length > 0) {
    exportData.query = { ...query, _id: ruleId };
  }

  // Add variable name to file title if exists
  const fileExtension = fileType === 'pdf' ? '.pdf' : '.xlsx';
  const fileTitle = variableName
    ? `${fileTitleBase} - ${variableName}${fileExtension}`
    : `${fileTitleBase}${fileExtension}`;

  return { exportData, fileTitle };
};

export {
  buildExportData,
};
